/**
 * src/api/client.js — Authenticated API Client
 *
 * Single source of truth for all HTTP calls to the FastAPI backend.
 * All authentication MUST go through the live backend — no local fallbacks.
 *
 * Base URL: https://callitin-backend.onrender.com (production)
 * Override via REACT_APP_API_BASE_URL for local dev against a different backend.
 *
 * Every request automatically attaches:
 *   Authorization: Bearer <jwt>
 * using the token stored in localStorage — UNLESS the caller passes
 * `auth: false` (used by the /auth/login call itself).
 */

import { getCurrentUser, logoutUser } from "../data/users";

const LIVE_BACKEND = "https://callitin-backend.onrender.com";
const AUTH_NOTICE_KEY = "ci_auth_notice";
export const SESSION_EXPIRED_EVENT = "ci:session-expired";
const CAPABILITY_CACHE_TTL_MS = 60_000;
let _backendCapabilitiesCache = null;
let _backendCapabilitiesFetchedAt = 0;

const getBaseUrl = () => {
  const raw =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_TMS_BASE_URL ||
    LIVE_BACKEND;
  return raw.replace(/\/+$/, "");
};

// Guard flag — prevents multiple simultaneous 401 responses from each firing
// their own redirect (e.g. when a polling interval fires several requests at once).
let _sessionExpiredRedirectPending = false;

function _safeParseJSON(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function _storeAuthNotice(detail) {
  try {
    window.sessionStorage.setItem(AUTH_NOTICE_KEY, JSON.stringify(detail));
  } catch {
    // ignore storage failures
  }
}

export function consumeAuthNotice() {
  try {
    const raw = window.sessionStorage.getItem(AUTH_NOTICE_KEY);
    window.sessionStorage.removeItem(AUTH_NOTICE_KEY);
    return _safeParseJSON(raw);
  } catch {
    return null;
  }
}

/**
 * Clear the stale session and send the browser to the login page.
 * Passes ?reason=expired so the login page can show a specific message.
 * Uses window.location.replace (full reload) because we are outside React's
 * component tree and cannot use useNavigate() here.
 */
function _handleSessionExpired({ url = "", method = "GET", authContext = null } = {}) {
  if (_sessionExpiredRedirectPending) return;
  _sessionExpiredRedirectPending = true;

  const endpoint = url ? url.replace(getBaseUrl(), "") : "";
  const detail = {
    message:
      authContext?.message ||
      "Your session expired during a secure request. Please sign in again.",
    endpoint,
    method,
    page: window.location.pathname,
    occurredAt: new Date().toISOString(),
  };

  _storeAuthNotice(detail);
  try {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail }));
  } catch {
    // ignore event dispatch failures
  }

  logoutUser();
  window.setTimeout(() => {
    window.location.replace("/login?reason=expired");
  }, 350);
}

/**
 * @param {string} path   - Leading-slash path, e.g. "/tasks/"
 * @param {object} opts
 * @param {string}  [opts.method="GET"]
 * @param {object}  [opts.body]         - JSON-serialisable request body
 * @param {BodyInit}[opts.rawBody]      - Pass FormData / Blob / text as-is
 * @param {object}  [opts.headers]      - Extra headers to merge
 * @param {boolean} [opts.auth=true]    - false skips the Authorization header
 * @param {object}  [opts.authContext]  - Optional context for 401 user messaging
 * @returns {Promise<Response>}
 */
async function request(
  path,
  {
    method = "GET",
    body,
    rawBody,
    headers: extraHeaders = {},
    auth = true,
    authContext = null,
  } = {}
) {
  const url = `${getBaseUrl()}${path.startsWith("/") ? path : `/${path}`}`;

  if (body != null && rawBody != null) {
    throw new Error("request() accepts either 'body' or 'rawBody', not both.");
  }

  const headers = { ...extraHeaders };
  if (rawBody == null && body != null && !headers["Content-Type"] && !headers["content-type"]) {
    headers["Content-Type"] = "application/json";
  }
  if (auth) {
    const token = getCurrentUser()?.token;
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  const opts = { method, headers };
  if (rawBody != null) opts.body = rawBody;
  if (rawBody == null && body != null) opts.body = JSON.stringify(body);

  const res = await fetch(url, opts);

  // 401 on an authenticated request means the session is invalid or expired.
  // Clear storage and redirect to login immediately — do not let the app
  // continue rendering with a dead session.
  // auth: false is excluded because that covers the /auth/login call itself,
  // which legitimately returns 401 for wrong credentials.
  if (res.status === 401 && auth) {
    _handleSessionExpired({ url, method, authContext });
  }

  return res;
}

function _normalizeCapabilitiesPayload(data, source) {
  const routes = data?.routes || {};
  return {
    source,
    checkedAt: new Date().toISOString(),
    routes: {
      capabilitiesManifest: !!routes.capabilitiesManifest,
      taskStatusUpdate: !!routes.taskStatusUpdate,
      taskPodSubmit: !!routes.taskPodSubmit,
      taskOverrideRequest: !!routes.taskOverrideRequest,
      taskOverrideApprove: !!routes.taskOverrideApprove,
      taskOverrideDeny: !!routes.taskOverrideDeny,
      driverTasks: !!routes.driverTasks,
      driverLocation: !!routes.driverLocation,
      tasksList: !!routes.tasksList,
      driversList: !!routes.driversList,
    },
  };
}

async function _capabilitiesFromOpenApi() {
  try {
    const res = await fetch(`${getBaseUrl()}/openapi.json`);
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    const paths = data?.paths || {};
    return {
      source: "openapi",
      checkedAt: new Date().toISOString(),
      routes: {
        capabilitiesManifest: !!paths["/api/capabilities"],
        taskStatusUpdate: !!paths["/tasks/{task_id}/status"],
        taskPodSubmit: !!paths["/tasks/{task_id}/pod"],
        taskOverrideRequest:
          !!paths["/tasks/{task_id}/override/request"] ||
          !!paths["/tasks/{task_id}/override"],
        taskOverrideApprove: !!paths["/tasks/{task_id}/override/approve"],
        taskOverrideDeny: !!paths["/tasks/{task_id}/override/deny"],
        driverTasks: !!paths["/driver/tasks"],
        driverLocation: !!paths["/driver/location"],
        tasksList: !!paths["/tasks/"],
        driversList: !!paths["/api/drivers"],
      },
    };
  } catch {
    return null;
  }
}

export async function getBackendCapabilities({ forceRefresh = false } = {}) {
  const now = Date.now();
  if (!forceRefresh && _backendCapabilitiesCache && now - _backendCapabilitiesFetchedAt < CAPABILITY_CACHE_TTL_MS) {
    return _backendCapabilitiesCache;
  }

  const openApiCaps = await _capabilitiesFromOpenApi();
  if (openApiCaps?.routes?.capabilitiesManifest) {
    try {
      const res = await fetch(`${getBaseUrl()}/api/capabilities`);
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        _backendCapabilitiesCache = _normalizeCapabilitiesPayload(data, "capabilities-endpoint");
        _backendCapabilitiesFetchedAt = now;
        return _backendCapabilitiesCache;
      }
    } catch {
      // fall back to OpenAPI-based route detection
    }
  }

  _backendCapabilitiesCache = openApiCaps;
  _backendCapabilitiesFetchedAt = now;
  return _backendCapabilitiesCache;
}

/**
 * Create a new user account. Admin only.
 * POST /auth/users  →  { id, email, name, role }
 *
 * Optional extra fields (e.g. organizationId for vendor_rep accounts) are
 * forwarded as-is so this function works for both internal employees and
 * external portal users without needing a separate endpoint wrapper.
 */
async function createUser({ name, email, password, role, ...extras }) {
  return request("/auth/users", {
    method: "POST",
    body: { name, email, password, role, ...extras },
  });
}

/**
 * Update a user's name, email, role, and/or password. Admin only.
 * PUT /auth/users/{id}
 * Body: { name?, email?, role?, password? }  — only supplied fields are changed.
 * Returns: { id, name, email, role }
 *
 * Falls back to PATCH only on 405 Method Not Allowed (older backend builds that
 * did not register PUT).  404 is NOT retried — it means the user genuinely does
 * not exist and re-sending PATCH with the same id would return the same 404.
 */
async function updateUser(userId, fields) {
  if (!userId) {
    console.error("[client.updateUser] called with a falsy userId — aborting to prevent PUT /auth/users/undefined.", { userId, fields });
    // Return a synthetic 400 response so the caller can surface a clean error.
    return new Response(JSON.stringify({ detail: "User ID is missing. Please refresh and try again." }), {
      status: 400,
      headers: { "Content-Type": "application/json" },
    });
  }

  const primary = await request(`/auth/users/${userId}`, {
    method: "PUT",
    body: fields,
  });

  // Only fall back to PATCH when the server explicitly rejects the PUT method
  // (405).  A 404 means the user was not found — PATCH with the same id would
  // also 404 — so we return the 404 immediately for a clear error message.
  if (primary.status === 405) {
    return request(`/auth/users/${userId}`, {
      method: "PATCH",
      body: fields,
    });
  }

  return primary;
}

/**
 * Fetch all user accounts including inactive ones. Admin only.
 * GET /auth/users  →  User[]  (each record has is_active: 0 | 1)
 */
function listUsers() {
  return request("/auth/users");
}

/**
 * Soft-deactivate a user (sets is_active = 0). Admin only.
 * DELETE /auth/users/{id}
 * Returns: { status: "deactivated", id }
 */
function deactivateUser(userId) {
  return request(`/auth/users/${userId}`, { method: "DELETE" });
}

/**
 * Reactivate a previously deactivated user. Admin only.
 * POST /auth/users/{id}/reactivate
 * Returns: { status: "reactivated", id }
 */
function reactivateUser(userId) {
  return request(`/auth/users/${userId}/reactivate`, { method: "POST" });
}

// ─── Driver App API ──────────────────────────────────────────────────────────

/**
 * Log the driver ONLINE and record starting mileage.
 * POST /driver/shift/start
 * Body: { startOdometer: number, startOdometerPhoto: string }
 * 1.0.2 — the backend requires a starting odometer photo (base64 JPEG,
 * data-URI prefix optional) alongside the reading; the request is rejected
 * with 422 if either is missing.
 * Returns: { id, driverName, startOdometer, startTime }
 */
function startDriverShift(startOdometer, startOdometerPhoto) {
  return request("/driver/shift/start", {
    method: "POST",
    body: { startOdometer: parseInt(startOdometer, 10), startOdometerPhoto },
    authContext: { message: "Your session expired while starting a driver shift." },
  });
}

/**
 * Close the active shift and record ending mileage.
 * POST /driver/shift/end
 * Body: { shiftId: string, endOdometer: number, endOdometerPhoto: string }
 * 1.0.2 — the backend requires an ending odometer photo (base64 JPEG,
 * data-URI prefix optional) alongside the reading; the request is rejected
 * with 422 if either is missing.
 * Returns: { status, totalMiles, driverName }
 */
function endDriverShift(shiftId, endOdometer, endOdometerPhoto) {
  return request("/driver/shift/end", {
    method: "POST",
    body: { shiftId, endOdometer: parseInt(endOdometer, 10), endOdometerPhoto },
    authContext: { message: "Your session expired while ending a driver shift." },
  });
}

/**
 * Fetch tasks assigned to the authenticated driver.
 * The backend uses the JWT to identify the caller — no driver name param needed.
 * GET /driver/tasks
 * Returns: Task[]
 */
function getMyTasks() {
  return request("/driver/tasks", {
    authContext: { message: "Your session expired while loading driver tasks." },
  });
}

/**
 * Update the status of a single task.
 * PUT /tasks/{id}/status
 * Body: { status: string, lat?: number, lng?: number }
 * Returns: { status, id, new_status }
 */
function updateTaskStatus(taskId, status, coords = null) {
  const body = { status };
  if (coords?.lat != null) body.lat = coords.lat;
  if (coords?.lng != null) body.lng = coords.lng;
  // GPS accuracy (m) lets the backend widen the geofence for this reading
  // (150 m + min(accuracy, 100 m)). Omitted when unknown => base radius only.
  if (typeof coords?.accuracy === "number" && Number.isFinite(coords.accuracy) && coords.accuracy >= 0) {
    body.accuracy = coords.accuracy;
  }
  return request(`/tasks/${taskId}/status`, {
    method: "PUT",
    body,
    authContext: { message: "Your session expired while updating a task status." },
  });
}

/**
 * Submit proof-of-delivery assets before marking a task Delivered.
 * POST /tasks/{id}/pod
 * Body: { photoBase64?: string, signatureBase64?: string, recipientName?: string }
 * Returns: { taskId, savedAt }
 */
function submitPOD(taskId, { photoBase64 = null, signatureBase64 = null, recipientName = null } = {}) {
  return request(`/tasks/${taskId}/pod`, {
    method: "POST",
    body: { photoBase64, signatureBase64, recipientName },
    authContext: { message: "Your session expired while saving proof of delivery." },
  });
}

/**
 * Submit pickup proof before transitioning from Arrived (PU) to Picked Up (two-leg flow).
 * POST /tasks/{id}/pod/pickup
 * Body: { photoBase64?: string }
 * Returns: { taskId, savedAt, proofId }
 */
function submitPickupPOD(taskId, { photoBase64 = null } = {}) {
  return request(`/tasks/${taskId}/pod/pickup`, {
    method: "POST",
    body: { photoBase64 },
    authContext: { message: "Your session expired while saving pickup proof." },
  });
}

/**
 * Retrieve the full execution-event timeline for a task.
 * GET /tasks/{id}/history
 * Returns: {
 *   taskId, hasAuditTrail: bool,
 *   events: EventRecord[]
 * }
 *
 * EventRecord: { id, eventType, actor, timestamp,
 *   newStatus?, overrideType?, taskType?,
 *   stage?, photoReceived?, signatureReceived? }
 *
 * hasAuditTrail=false means only blob-derived events are available
 * (task pre-dates audit instrumentation); the events array is still
 * populated with whatever truthful subset exists.
 */
function getTaskHistory(taskId) {
  return request(`/tasks/${taskId}/history`, {
    authContext: { message: "Your session expired while loading task history." },
  });
}

/**
 * Retrieve all persisted proof records for a task (pickup + delivery).
 * GET /tasks/{id}/pod
 * Returns: {
 *   taskId, proofs: ProofRecord[],
 *   // Legacy blob flags for backward compat:
 *   podPhotoReceived, podSignatureReceived, podRecipientName, podReceivedAt,
 *   pickupProofCaptured, pickupProofReceivedAt
 * }
 *
 * ProofRecord: { id, stage, driverEmail, photoReceived, signatureReceived,
 *   recipientName, savedAt, photoData, signatureData }
 *
 * photoData / signatureData are base64 strings when the image was captured
 * after persistent storage was deployed, or null for older records.
 */
function getTaskProofs(taskId) {
  return request(`/tasks/${taskId}/pod`, {
    authContext: { message: "Your session expired while loading proof records." },
  });
}

// ─── Phase 3: Dispatch & Driver Execution ────────────────────────────────────

/**
 * Fetch all active tasks from the backend (dispatcher view).
 * GET /tasks/
 * Returns: Task[]
 */
function getTasks() {
  return request("/tasks/", {
    authContext: { message: "Your session expired while loading tasks." },
  });
}

/**
 * Assign a driver (by user UUID) to a task.
 * POST /tasks/{id}/assign
 * Body: { driver_id: string }
 * Returns: { status, task_id, driver_id, driver_name }
 */
function assignTaskDriver(taskId, driverId) {
  return request(`/tasks/${taskId}/assign`, {
    method: "POST",
    body: { driver_id: driverId },
    authContext: { message: "Your session expired while assigning a driver." },
  });
}

/**
 * Fetch all driver users with their current operational status.
 * GET /api/drivers
 * Returns: Driver[]  (id, name, email, operational_status, current_task_id, last_lat, last_lng, updated_at)
 */
function listDrivers() {
  return request("/api/drivers", {
    authContext: { message: "Your session expired while loading drivers." },
  });
}

/**
 * Update a driver's operational status.
 * PUT /drivers/{id}/status
 * Body: { status: string, current_task_id?: string, lat?: number, lng?: number }
 */
function updateDriverStatus(driverId, status, extras = {}) {
  return request(`/drivers/${driverId}/status`, {
    method: "PUT",
    body: { status, ...extras },
    authContext: { message: "Your session expired while updating a driver status." },
  });
}

/**
 * Admin-only emergency removal of a driver from active shift / operations.
 * POST /drivers/{id}/remove-from-shift
 * Body: { note?: string }
 */
function removeDriverFromShift(driverId, note = "") {
  return request(`/drivers/${driverId}/remove-from-shift`, {
    method: "POST",
    body: { note },
    authContext: { message: "Your session expired while removing a driver from shift." },
  });
}

/**
 * Dispatcher/admin: force a driver offline from Driver Status.
 * POST /drivers/{id}/logout
 */
function logoutDriver(driverId) {
  return request(`/drivers/${driverId}/logout`, {
    method: "POST",
    authContext: { message: "Your session expired while logging out a driver." },
  });
}

/**
 * Publish the calling driver's current GPS position.
 * Called periodically from the Driver App while on an active shift.
 * The driver is identified server-side from the JWT — no ID parameter needed.
 * @param {number} lat
 * @param {number} lng
 */
function updateMyLocation(lat, lng) {
  return request("/driver/location", {
    method: "PUT",
    body: { lat, lng },
    authContext: { message: "Your session expired while publishing driver location." },
  });
}

/**
 * Request a dispatcher override for a blocked task action.
 * POST /tasks/{id}/override/request
 * Body: { overrideType: "arrival" | "delivery", reason?: string }
 */
async function requestTaskOverride(taskId, overrideType, reason = "") {
  const body = { overrideType, reason };
  const authCtx = { message: "Your session expired while requesting a dispatch override." };

  // Attempt the canonical /override/request route first.
  // If it throws (network error, cold-start, etc.) fall through to the
  // legacy /override alias rather than surfacing an opaque failure.
  let primary = null;
  try {
    primary = await request(`/tasks/${taskId}/override/request`, {
      method: "POST",
      body,
      authContext: authCtx,
    });
  } catch {
    // Network-level failure on primary route — try fallback below.
  }

  if (primary !== null && primary.status !== 404 && primary.status !== 405) {
    return primary;
  }

  // Fallback: legacy alias registered on the same handler.
  return request(`/tasks/${taskId}/override`, {
    method: "POST",
    body,
    authContext: authCtx,
  });
}

/**
 * Dispatcher-only: approve a pending override request.
 * POST /tasks/{id}/override/approve
 * Body: { overrideType: "arrival" | "delivery", note?: string }
 */
function approveTaskOverride(taskId, overrideType, note = "") {
  return request(`/tasks/${taskId}/override/approve`, {
    method: "POST",
    body: { overrideType, note },
    authContext: { message: "Your session expired while approving an override." },
  });
}

/**
 * Dispatcher-only: deny a pending override request.
 * POST /tasks/{id}/override/deny
 * Body: { overrideType: "arrival" | "delivery", note?: string }
 */
function denyTaskOverride(taskId, overrideType, note = "") {
  return request(`/tasks/${taskId}/override/deny`, {
    method: "POST",
    body: { overrideType, note },
    authContext: { message: "Your session expired while denying an override." },
  });
}

// ─── Driver Records (1.0.2) — shift/mileage history + driver expenses ───────
//
// Dispatcher/admin-only read access to the accountability records the driver
// app now captures (odometer photos + expense receipts). Photos are fetched
// as base64 JSON (fmt=json) rather than as <img src="..."> URLs because the
// backend requires an Authorization header — the same pattern already used
// by getTaskProofs()/ProofSection for pickup/delivery proof photos.

function _qs(params = {}) {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") usp.set(k, v);
  });
  const s = usp.toString();
  return s ? `?${s}` : "";
}

/**
 * Shift & mileage history for the Driver Records dashboard.
 * GET /shifts
 * Returns: ShiftRecord[] — see main.py list_shifts() for the exact shape.
 */
function listShiftHistory({ driver, dateFrom, dateTo, limit } = {}) {
  return request(`/shifts${_qs({ driver, dateFrom, dateTo, limit })}`, {
    authContext: { message: "Your session expired while loading shift history." },
  });
}

/**
 * Fetch a shift's starting or ending odometer photo as base64 JSON.
 * GET /shifts/{shiftId}/odometer-photo?stage=start|end&fmt=json
 * Returns: { shiftId, stage, photoData, capturedAt }
 * 404 means the shift predates odometer photo verification (legacy record).
 */
function getShiftOdometerPhoto(shiftId, stage) {
  return request(`/shifts/${shiftId}/odometer-photo${_qs({ stage, fmt: "json" })}`, {
    authContext: { message: "Your session expired while loading an odometer photo." },
  });
}

/**
 * Driver expense/receipt list for the Driver Records dashboard.
 * GET /driver/expenses
 * Returns: ExpenseRecord[] — see main.py list_driver_expenses().
 */
function listDriverExpenses({ driver, category, reviewStatus, dateFrom, dateTo, limit } = {}) {
  return request(`/driver/expenses${_qs({ driver, category, reviewStatus, dateFrom, dateTo, limit })}`, {
    authContext: { message: "Your session expired while loading driver expenses." },
  });
}

/**
 * Fetch an expense's receipt photo as base64 JSON.
 * GET /driver/expenses/{expenseId}/receipt?fmt=json
 * Returns: { expenseId, photoData, capturedAt }
 */
function getExpenseReceipt(expenseId) {
  return request(`/driver/expenses/${expenseId}/receipt${_qs({ fmt: "json" })}`, {
    authContext: { message: "Your session expired while loading a receipt photo." },
  });
}

/**
 * Approve or reject a submitted expense.
 * POST /driver/expenses/{expenseId}/review
 * Body: { decision: "approved" | "rejected" }  (lowercase — see main.py)
 * Returns: { id, reviewStatus: "Approved"|"Rejected", reviewedBy, reviewedAt }
 */
function reviewDriverExpense(expenseId, decision) {
  return request(`/driver/expenses/${expenseId}/review`, {
    method: "POST",
    body: { decision: String(decision).toLowerCase() },
    authContext: { message: "Your session expired while reviewing an expense." },
  });
}

/**
 * Download a protected binary from the TMS backend (e.g. a POD photo).
 * Goes through request(), so it carries the session JWT and gets the same
 * 401 / session-expired handling as every other call - the token is never
 * put in a URL.
 *
 * @param {string} path      e.g. "/tasks/{id}/pod/photo?stage=delivery"
 * @param {string} filename  suggested download name
 * @returns {Promise<void>}  rejects with a user-presentable Error on failure
 */
async function downloadProtectedFile(path, filename) {
  const res = await request(path, {
    authContext: { message: "Your session expired while downloading a file." },
  });
  if (!res.ok) {
    let detail = "";
    try {
      detail = (await res.json())?.detail || "";
    } catch {
      // non-JSON error body
    }
    throw new Error(detail || `Download failed (HTTP ${res.status}).`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Give the browser a tick to start the download before releasing the blob.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

export {
  getBaseUrl, request, downloadProtectedFile,
  createUser, updateUser, listUsers, deactivateUser, reactivateUser,
  startDriverShift, endDriverShift, getMyTasks, updateTaskStatus, submitPOD, submitPickupPOD,
  getTaskProofs, getTaskHistory,
  getTasks, assignTaskDriver, listDrivers, updateDriverStatus, removeDriverFromShift, logoutDriver, updateMyLocation,
  requestTaskOverride, approveTaskOverride, denyTaskOverride,
  listShiftHistory, getShiftOdometerPhoto, listDriverExpenses, getExpenseReceipt, reviewDriverExpense,
};
